import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  useToast,
  Select,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaEnvelope, FaPaperPlane } from 'react-icons/fa';
import { 
  ContactPresentationProps, 
  ContactFormData, 
  ContactInfo,
  InterestOption
} from '../types';
import ContactInfoItemPresentation from './ContactInfoItemPresentation';
import { SectionAnchor } from '../../../shared/components';

const ContactPresentation: React.FC<ContactPresentationProps> = ({
  id,
  heading,
  description,
  contactInfoItems,
  interestOptions,
  onSubmit
}) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    interest: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const toast = useToast();
  const formBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Reset error state when user makes changes
    if (submitState === 'error') {
      setSubmitState('idle');
      setErrorDetails(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    setErrorDetails(null);
    setIsSubmitting(true);
    setSubmitState('sending');
    
    // Log submission attempt with timestamp
    console.log(`[${new Date().toISOString()}] Contact form submission started:`, {
      email: formData.email,
      subject: formData.subject
    });
    
    try {
      // Set a timeout for the submission
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Submission timed out. Please try again.')), 20000); // 20 seconds timeout
      });
      
      // Race between the submission and the timeout
      await Promise.race([
        onSubmit(formData),
        timeoutPromise
      ]);
      
      // Log successful submission
      console.log(`[${new Date().toISOString()}] Contact form submission successful`);
      setSubmitState('success');
      
      toast({
        title: 'Message sent!',
        description: "We've received your message and will contact you soon.",
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: '',
        interest: '',
      });
    } catch (error) {
      // Log detailed error
      console.error(`[${new Date().toISOString()}] Contact form submission failed:`, error);
      setSubmitState('error');
      
      // Extract error message
      let errorMessage = 'Something went wrong. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
        setErrorDetails(errorMessage);
      }
      
      toast({
        title: 'Error Sending Message',
        description: errorMessage,
        status: 'error',
        duration: 10000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SectionAnchor id={id} />
      <Box
        as="section"
        pt={{ base: 4, md: 6 }} pb={{ base: 8, md: 12 }}
        bg="gray.50"
    >
      <Container maxW="container.xl">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
          <Box>
            <Heading 
              as="h2" 
              size="xl" 
              mb={6}
              bgGradient="linear(to-r, brand.primary.dark, brand.primary.base)"
              bgClip="text"
            >
              {heading}
            </Heading>
            
            <Text fontSize="lg" mb={10} color="gray.600">
              {description}
            </Text>
            
            <VStack spacing={6} align="flex-start" mb={10}>
              {contactInfoItems.map((info: ContactInfo, index: number) => (
                <ContactInfoItemPresentation
                  key={index}
                  icon={info.icon}
                  title={info.title}
                  content={info.content}
                  link={info.link}
                />
              ))}
            </VStack>
            
            <Box
              p={6}
              borderRadius="lg"
              bg={formBg}
              boxShadow="md"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Heading as="h3" size="md" mb={4}>
                Schedule a Consultation
              </Heading>
              <Text color="gray.600" mb={4}>
                Book a free 30-minute consultation with our experts to explore how 
                agentic systems can benefit your organization.
              </Text>
              <Button 
                leftIcon={<FaEnvelope />} 
                colorScheme="teal" 
                variant="outline"
                as="a" 
                href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2c6vLHYel5sfK0HgYvFV4AwAFt5rI6buw9kUzeqDn9FcB9l0QKIMRuP7GvG3AhMmU_C-uBUpwi"
                target="_blank"
                rel="noopener noreferrer"
              >
                Schedule Now
              </Button>
            </Box>
          </Box>
          
          <Box
            p={8}
            borderRadius="lg"
            bg={formBg}
            boxShadow="xl"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <form onSubmit={handleSubmit}>
              <VStack spacing={5}>
                <Heading as="h3" size="md" alignSelf="flex-start">
                  Send Us a Message
                </Heading>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} width="full">
                  <FormControl isRequired>
                    <FormLabel>Name</FormLabel>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                    />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your email"
                    />
                  </FormControl>
                </SimpleGrid>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} width="full">
                  <FormControl>
                    <FormLabel>Company</FormLabel>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your company"
                    />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel>Interest</FormLabel>
                    <Select
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      placeholder="Select option"
                    >
                      {interestOptions.map((option: InterestOption) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </SimpleGrid>
                
                <FormControl isRequired>
                  <FormLabel>Subject</FormLabel>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Message subject"
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel>Message</FormLabel>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message"
                    rows={5}
                  />
                </FormControl>
                
                {errorDetails && (
                  <Box 
                    p={4} 
                    mb={4} 
                    borderRadius="md" 
                    bg="red.50" 
                    borderWidth="1px" 
                    borderColor="red.300"
                  >
                    <Text color="red.600" fontSize="sm">
                      Error: {errorDetails}
                    </Text>
                    <Text color="red.500" fontSize="xs" mt={1}>
                      Please try again or contact us directly at info@az1.ai
                    </Text>
                  </Box>
                )}
                
                <Button
                  type="submit"
                  colorScheme="teal"
                  size="lg"
                  width="full"
                  isLoading={isSubmitting}
                  loadingText={
                    submitState === 'sending' 
                      ? "Sending your message..." 
                      : "Processing..."
                  }
                  {...(submitState !== 'success' && { leftIcon: <FaPaperPlane /> })}
                  bg={submitState === 'error' ? "red.500" : "brand.primary.base"}
                  _hover={{ 
                    bg: submitState === 'error' 
                      ? "red.600" 
                      : "brand.primary.light" 
                  }}
                  disabled={submitState === 'success'}
                >
                  {submitState === 'success' 
                    ? "Message Sent! ✓" 
                    : submitState === 'error'
                      ? "Try Again"
                      : "Send Message"
                  }
                </Button>
                
                {submitState === 'sending' && (
                  <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
                    Please wait while we send your message... This may take up to 30 seconds.
                  </Text>
                )}
              </VStack>
            </form>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
    </>
  );
};

export default ContactPresentation;